import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Select,
  SimpleGrid,
  Switch,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Tooltip,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FaPlay, FaPlus, FaUsers, FaStar, FaRegStar } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  forkSearch,
  listRegions,
  runSearch,
  validateCriteria,
} from "../../api/searchApi";
import { SKILL_TYPE_COLOR } from "./colors";

const SENIORITY_OPTIONS = ["junior", "mid", "senior", "expert"];
const SCOPE_OPTIONS = ["city", "region", "country", "remote"];

// Slider bounds for years-of-experience. The user picks min and max as
// independent integers; an "Indifférent" toggle disables the constraint
// entirely (clears min and max to null). 0 to 20 covers common consulting
// asks including the 8 to 12 year case the dropdown couldn't express.
const EXPERIENCE_MIN = 0;
const EXPERIENCE_MAX = 20;

const TIER_KEYS = ["required", "optional"];
const TYPE_KEYS = ["technical", "functional", "business"];

const _normalizeCountryKey = (value) =>
  String(value || "").trim().toLowerCase();

export const skillTypeFor = (label, typed) => {
  if (!label || !typed) return null;
  const lower = String(label).trim().toLowerCase();
  for (const type of TYPE_KEYS) {
    const list = typed[type] || [];
    if (list.some((s) => String(s).trim().toLowerCase() === lower)) {
      return type;
    }
  }
  return null;
};

const _defaultScopeFor = (city, region, country) => {
  if (city) return "city";
  if (region) return "region";
  if (country) return "country";
  return "";
};

const SortableSkillTag = ({
  id,
  tier,
  label,
  color,
  type,
  isRequired,
  inputsDisabled,
  toggleTooltip,
  reorderAriaLabel,
  onToggle,
  onRemove,
}) => {
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: inputsDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: inputsDisabled ? "default" : isDragging ? "grabbing" : "grab",
    touchAction: "none",
  };

  const stopDrag = (e) => {
    e.stopPropagation();
  };

  // We deliberately spread the drag `listeners` on the wrapper but NOT
  // dnd-kit's `attributes` (role="button", tabIndex, aria-roledescription).
  // The Tag already contains the star and close buttons; setting role=button
  // on the wrapper would nest interactive elements, which the browser
  // resolves by swallowing clicks on the inner buttons, breaking the very
  // toggles users rely on after reordering.
  return (
    <WrapItem>
      <div
        ref={setNodeRef}
        style={style}
        {...(inputsDisabled ? {} : listeners)}
        aria-label={inputsDisabled ? undefined : reorderAriaLabel}
        data-testid={`criteria-skill-item-${tier}-${label}`}
      >
        <Tag
          size="sm"
          colorScheme={color}
          borderRadius="full"
          data-skill-type={type || "untyped"}
        >
          {!inputsDisabled && (
            <Tooltip label={toggleTooltip} hasArrow>
              <IconButton
                aria-label={toggleTooltip}
                icon={isRequired ? <FaStar /> : <FaRegStar />}
                size="xs"
                variant="ghost"
                ml="-6px"
                mr={1}
                onClick={onToggle}
                onPointerDown={stopDrag}
                data-testid={`criteria-skill-toggle-${tier}-${label}`}
              />
            </Tooltip>
          )}
          <TagLabel>{label}</TagLabel>
          {!inputsDisabled && (
            <TagCloseButton onClick={onRemove} onPointerDown={stopDrag} />
          )}
        </Tag>
      </div>
    </WrapItem>
  );
};

const buildState = (search) => {
  const cs = search?.criteriaSummary || {};
  const country = cs.geoCountry || "";
  const region = cs.geoRegion || "";
  const city = cs.geoCity || "";
  return {
    rfp_title: cs.rfpTitle || search?.title || "",
    client: cs.client || search?.client || "",
    experience_years_min:
      cs.experienceYearsMin === null || cs.experienceYearsMin === undefined
        ? null
        : Number(cs.experienceYearsMin),
    experience_years_max:
      cs.experienceYearsMax === null || cs.experienceYearsMax === undefined
        ? null
        : Number(cs.experienceYearsMax),
    geo_country: country,
    geo_region: region,
    geo_city: city,
    geo_scope: cs.geoScope || _defaultScopeFor(city, region, country),
    summary: cs.summary || "",
    skills: {
      required: Array.isArray(cs.skillsRequired) ? [...cs.skillsRequired] : [],
      optional: Array.isArray(cs.skillsOptional) ? [...cs.skillsOptional] : [],
    },
    typed: {
      technical: Array.isArray(cs.skillsTechnical) ? [...cs.skillsTechnical] : [],
      functional: Array.isArray(cs.skillsFunctional)
        ? [...cs.skillsFunctional]
        : [],
      business: Array.isArray(cs.skillsBusiness) ? [...cs.skillsBusiness] : [],
    },
  };
};

const CriteriaForm = ({
  search,
  buId,
  disabled = false,
  onSearchUpdated,
  onSearchForked,
  onRunCompleted,
  onViewCandidates,
}) => {
  const { t } = useTranslation("portal");
  const toast = useToast();

  const initialState = useMemo(() => buildState(search), [search]);
  const [form, setForm] = useState(initialState);
  const [skillInputs, setSkillInputs] = useState({
    required: { name: "", type: "technical" },
    optional: { name: "", type: "technical" },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmRerunOpen, setConfirmRerunOpen] = useState(false);
  const cancelRef = useRef(null);

  useEffect(() => {
    setForm(buildState(search));
    setSkillInputs({
      required: { name: "", type: "technical" },
      optional: { name: "", type: "technical" },
    });
  }, [search]);

  // Region dropdown is country-dependent and cached per country to avoid
  // refetching when the user toggles between scopes. Failures are tracked
  // separately so the effect doesn't loop on transient errors and the user
  // gets an explicit retry path (toast + retry button) instead of a silently
  // empty Select.
  const [regionsByCountry, setRegionsByCountry] = useState({});
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [regionsFailedFor, setRegionsFailedFor] = useState({});
  const [regionsRetryNonce, setRegionsRetryNonce] = useState(0);
  const countryKey = _normalizeCountryKey(form.geo_country);

  useEffect(() => {
    if (!countryKey) return;
    if (regionsByCountry[countryKey] !== undefined) return;
    if (regionsFailedFor[countryKey]) return;
    let cancelled = false;
    setRegionsLoading(true);
    listRegions(countryKey, { buId })
      .then((data) => {
        if (cancelled) return;
        setRegionsByCountry((prev) => ({
          ...prev,
          [countryKey]: Array.isArray(data?.regions) ? data.regions : [],
        }));
      })
      .catch((err) => {
        if (cancelled) return;
        setRegionsFailedFor((prev) => ({ ...prev, [countryKey]: true }));
        toast({
          status: "error",
          title: t("asapProfile.criteria.errors.regionsLoadFailed"),
          duration: 5000,
          isClosable: true,
        });
        console.error("Failed to load regions", err);
      })
      .finally(() => {
        if (cancelled) return;
        setRegionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [countryKey, buId, regionsByCountry, regionsFailedFor, regionsRetryNonce, t, toast]);

  const retryLoadRegions = () => {
    if (!countryKey) return;
    setRegionsFailedFor((prev) => {
      if (!prev[countryKey]) return prev;
      const next = { ...prev };
      delete next[countryKey];
      return next;
    });
    setRegionsRetryNonce((n) => n + 1);
  };

  const availableRegions = countryKey
    ? regionsByCountry[countryKey] || []
    : [];

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setScope = (nextScope) => {
    setForm((prev) => {
      // Locked decision L4: scope=remote hides + clears all three location
      // inputs. Other scopes leave existing values alone, the user can keep
      // them or refine.
      if (nextScope === "remote") {
        return {
          ...prev,
          geo_scope: nextScope,
          geo_country: "",
          geo_region: "",
          geo_city: "",
        };
      }
      return { ...prev, geo_scope: nextScope };
    });
  };

  const addSkill = (tier) => {
    const raw = (skillInputs[tier]?.name || "").trim();
    const type = skillInputs[tier]?.type || "technical";
    if (!raw) return;
    setForm((prev) => {
      const lower = raw.toLowerCase();
      const tierExisting = prev.skills[tier] || [];
      if (tierExisting.some((s) => s.toLowerCase() === lower)) return prev;
      const otherTier = tier === "required" ? "optional" : "required";
      const otherExisting = prev.skills[otherTier] || [];
      const filteredOther = otherExisting.filter(
        (s) => s.toLowerCase() !== lower
      );

      const nextTyped = { ...prev.typed };
      for (const k of TYPE_KEYS) {
        nextTyped[k] = (nextTyped[k] || []).filter(
          (s) => s.toLowerCase() !== lower
        );
      }
      nextTyped[type] = [...nextTyped[type], raw];

      return {
        ...prev,
        skills: {
          ...prev.skills,
          [tier]: [...tierExisting, raw],
          [otherTier]: filteredOther,
        },
        typed: nextTyped,
      };
    });
    setSkillInputs((prev) => ({
      ...prev,
      [tier]: { name: "", type: prev[tier]?.type || "technical" },
    }));
  };

  const removeSkill = (tier, label) => {
    const lower = label.toLowerCase();
    setForm((prev) => {
      const nextTyped = { ...prev.typed };
      for (const k of TYPE_KEYS) {
        nextTyped[k] = (nextTyped[k] || []).filter(
          (s) => s.toLowerCase() !== lower
        );
      }
      return {
        ...prev,
        skills: {
          ...prev.skills,
          [tier]: (prev.skills[tier] || []).filter(
            (s) => s.toLowerCase() !== lower
          ),
        },
        typed: nextTyped,
      };
    });
  };

  // Drag-and-drop reorder within a single tier. The 5px activation
  // distance prevents the sortable from swallowing clicks intended for
  // the star toggle and close (×) buttons embedded inside each tag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const reorderSkill = (tier, oldIndex, newIndex) => {
    if (oldIndex === newIndex) return;
    setForm((prev) => {
      const list = prev.skills[tier] || [];
      if (
        oldIndex < 0 ||
        newIndex < 0 ||
        oldIndex >= list.length ||
        newIndex >= list.length
      ) {
        return prev;
      }
      return {
        ...prev,
        skills: {
          ...prev.skills,
          [tier]: arrayMove(list, oldIndex, newIndex),
        },
      };
    });
  };

  // Flip a skill chip between required and optional in place. The
  // required/optional split is independent of the technical/functional/
  // business classification, so we leave `typed` untouched and only move
  // the label between `skills.required` and `skills.optional`.
  const toggleSkillTier = (currentTier, label) => {
    const lower = label.toLowerCase();
    const otherTier = currentTier === "required" ? "optional" : "required";
    setForm((prev) => {
      const currentList = (prev.skills[currentTier] || []).filter(
        (s) => s.toLowerCase() !== lower
      );
      const otherExisting = prev.skills[otherTier] || [];
      const otherList = otherExisting.some((s) => s.toLowerCase() === lower)
        ? otherExisting
        : [...otherExisting, label];
      return {
        ...prev,
        skills: {
          ...prev.skills,
          [currentTier]: currentList,
          [otherTier]: otherList,
        },
      };
    });
  };

  // Slider helpers: coerce nulls into the slider's full range so the
  // thumbs render at the edges when "Indifférent" is on.
  const isExperienceAny =
    form.experience_years_min === null && form.experience_years_max === null;
  const sliderMin =
    form.experience_years_min === null || form.experience_years_min === undefined
      ? EXPERIENCE_MIN
      : Math.max(EXPERIENCE_MIN, Math.min(EXPERIENCE_MAX, Number(form.experience_years_min)));
  const sliderMax =
    form.experience_years_max === null || form.experience_years_max === undefined
      ? EXPERIENCE_MAX
      : Math.max(EXPERIENCE_MIN, Math.min(EXPERIENCE_MAX, Number(form.experience_years_max)));

  const handleSliderChange = ([nextMin, nextMax]) => {
    setForm((prev) => ({
      ...prev,
      experience_years_min: nextMin,
      experience_years_max: nextMax,
    }));
  };

  const handleIndifferentToggle = (checked) => {
    setForm((prev) => {
      if (checked) {
        return {
          ...prev,
          experience_years_min: null,
          experience_years_max: null,
        };
      }
      // Re-enabling the constraint defaults to the full range; the user
      // can then drag the thumbs.
      return {
        ...prev,
        experience_years_min: EXPERIENCE_MIN,
        experience_years_max: EXPERIENCE_MAX,
      };
    });
  };

  const fireRun = async (searchIdOverride = null, options = {}) => {
    // ``options.baseSearch`` holds the freshest copy of the search after
    // a preceding validateCriteria call. Without it we'd close over the
    // stale ``search`` prop and the optimistic flip below would
    // overwrite the just-validated parent state with old fields, making
    // the badge briefly revert to the previous status.
    const baseSearch = options.baseSearch || search;
    const targetId = searchIdOverride ?? baseSearch.id;
    const isFreshRun =
      options.isFreshRun ?? baseSearch.status !== "completed";
    // Optimistically flip the status to "queued" before the request
    // resolves. Without this, the badge shows the previous status
    // ("validated" or "completed") for the ~1s the POST takes, which is
    // confusing: users expect immediate visual feedback that the search
    // started.
    if (isFreshRun) {
      onSearchUpdated?.({ ...baseSearch, status: "queued" });
    }
    try {
      const result = await runSearch(targetId, { buId });
      // ASAP PROFILE v2: /run is async, the backend returns 202 with
      // status=queued|running and the bg task fills profiles later. The
      // page-level polling (every 30s) flips status → completed and
      // emits its own "completed" toast at that point.
      // Only the legacy sync path (re-render of an already-completed
      // search, no new PDL bill) returns a finished payload here, in
      // which case the caller still wants the modal to open immediately.
      const isAsync =
        result?.status === "queued" || result?.status === "running";
      if (isAsync) {
        onSearchUpdated?.(result);
        if (isFreshRun) {
          toast({
            title: t("asapProfile.run.startedToast", {
              defaultValue: "Search started. We'll notify you when results are ready.",
            }),
            status: "info",
            duration: 4000,
            isClosable: true,
          });
        }
        return;
      }
      onRunCompleted?.(result);
      if (isFreshRun) {
        toast({
          title: t("asapProfile.run.successToast"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Run search failed", err);
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" && detail !== null
        ? detail.code
        : detail;
      const lookup = {
        PDL_AUTH_ERROR: t("asapProfile.run.errors.authError"),
        PDL_RATE_LIMIT: t("asapProfile.run.errors.rateLimit"),
        PDL_NOT_CONFIGURED: t("asapProfile.run.errors.notConfigured"),
        PDL_BILLING_EXHAUSTED: t("asapProfile.run.errors.billingExhausted"),
        CRITERIA_NOT_VALIDATED: t("asapProfile.run.errors.notValidated"),
      };
      toast({
        title: lookup[code] || t("asapProfile.run.errors.generic"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Track whether the form has been edited since it was loaded. Stringified
  // comparison keeps it cheap and stable across nested skill arrays.
  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialState),
    [form, initialState]
  );

  const _buildCriteriaPayload = () => {
    const isRemote = form.geo_scope === "remote";
    return {
      rfp_title: form.rfp_title || null,
      client: form.client || null,
      experience_years_min:
        form.experience_years_min === null ||
        form.experience_years_min === undefined
          ? null
          : Number(form.experience_years_min),
      experience_years_max:
        form.experience_years_max === null ||
        form.experience_years_max === undefined
          ? null
          : Number(form.experience_years_max),
      geo_scope: form.geo_scope || null,
      geo_country: isRemote ? null : form.geo_country || null,
      geo_region: isRemote ? null : form.geo_region || null,
      geo_city: isRemote ? null : form.geo_city || null,
      summary: form.summary || null,
      skills: form.skills,
      skills_technical: form.typed.technical,
      skills_functional: form.typed.functional,
      skills_business: form.typed.business,
    };
  };

  const validateAndRun = async () => {
    const updated = await validateCriteria(
      search.id,
      _buildCriteriaPayload(),
      { buId }
    );
    onSearchUpdated?.(updated);
    // Pass the validated payload to fireRun so the optimistic
    // status-flip uses the LATEST state (with the new criteria) rather
    // than the stale ``search`` prop from before validateCriteria ran.
    await fireRun(null, { baseSearch: updated });
  };

  const forkAndRun = async () => {
    // Completed search + edited criteria → create a NEW search row instead
    // of mutating the current one. The original keeps its profiles for
    // later comparison; we run PDL on the new id.
    const created = await forkSearch(
      search.id,
      _buildCriteriaPayload(),
      { buId }
    );
    onSearchForked?.(created);
    await fireRun(created.id, { isFreshRun: true, baseSearch: created });
  };

  const surfaceValidateError = (err) => {
    console.error("Failed to validate criteria", err);
    const detail = err?.response?.data?.detail;
    // Forks share this error handler. A fork on a completed search creates
    // a new SearchRequest row, so the monthly search-budget guard can fire
    // here too — surface a dedicated toast instead of the generic
    // "validate failed" copy.
    const code = typeof detail === "object" && detail !== null ? detail.code : null;
    if (code === "PDL_SEARCH_BUDGET_EXCEEDED") {
      toast({
        title: t("asapProfile.create.errors.searchBudgetExceededTitle"),
        description: t("asapProfile.create.errors.searchBudgetExceededBody", {
          used: detail?.currentMonthSearchesUsed ?? 0,
          budget: detail?.monthlySearchBudget ?? 0,
        }),
        status: "warning",
        duration: 8000,
        isClosable: true,
      });
      return;
    }
    const lookup = {
      REQUIRED_SKILLS_EMPTY: t("asapProfile.criteria.errors.requiredSkillsEmpty"),
      INVALID_SENIORITY: t("asapProfile.criteria.errors.invalidSeniority"),
      INVALID_GEO_SCOPE: t("asapProfile.criteria.errors.invalidGeoScope"),
      GEO_CITY_REQUIRED_FOR_SCOPE_CITY: t(
        "asapProfile.criteria.errors.geoCityRequired"
      ),
      GEO_REGION_REQUIRED_FOR_SCOPE_REGION: t(
        "asapProfile.criteria.errors.geoRegionRequired"
      ),
      GEO_COUNTRY_REQUIRED_FOR_SCOPE_COUNTRY: t(
        "asapProfile.criteria.errors.geoCountryRequired"
      ),
      INVALID_STATE_FOR_VALIDATE: t(
        "asapProfile.criteria.errors.validateFailed"
      ),
    };
    toast({
      title: lookup[detail] || t("asapProfile.criteria.errors.validateFailed"),
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  };

  const handleStart = async () => {
    if (!form.skills.required || form.skills.required.length === 0) {
      toast({
        title: t("asapProfile.criteria.errors.requiredSkillsEmpty"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (search.status === "completed") {
      // Always confirm before re-running on a completed search. The dialog
      // body adapts based on whether criteria changed (billable rerun) or
      // not (cheap re-render).
      setConfirmRerunOpen(true);
      return;
    }
    setIsSubmitting(true);
    try {
      await validateAndRun();
    } catch (err) {
      surfaceValidateError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmRerun = async () => {
    setConfirmRerunOpen(false);
    setIsSubmitting(true);
    try {
      // Dirty form on a completed search → fork into a NEW SearchRequest
      // so the original profiles stay accessible. Clean form → re-render
      // the existing profiles (no PDL re-bill).
      if (isDirty) {
        await forkAndRun();
      } else {
        await fireRun();
      }
    } catch (err) {
      surfaceValidateError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputsDisabled = disabled || isSubmitting;
  const isRemote = form.geo_scope === "remote";
  const showCity = !isRemote && form.geo_scope === "city";
  const showRegion =
    !isRemote && (form.geo_scope === "region" || form.geo_scope === "city");
  const showCountry = !isRemote;
  const scopeMissing =
    (form.geo_scope === "city" && !form.geo_city) ||
    (form.geo_scope === "region" && !form.geo_region) ||
    (form.geo_scope === "country" && !form.geo_country);
  const canStart =
    !disabled && !isSubmitting && !!search && !scopeMissing;
  const isCompleted = search?.status === "completed";

  return (
    <VStack align="stretch" spacing={4} data-testid="criteria-form">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        <VStack align="stretch" spacing={3} h="100%">
          <FormControl isRequired isDisabled={inputsDisabled}>
            <FormLabel sx={{ marginBottom: "4px !important" }}>
              {t("asapProfile.criteria.fields.rfpTitle")}
            </FormLabel>
            <Input
              size="sm"
              value={form.rfp_title}
              onChange={(e) => setField("rfp_title", e.target.value)}
              data-testid="criteria-input-rfp-title"
            />
          </FormControl>
          <FormControl
            isDisabled={inputsDisabled}
            display="flex"
            flexDirection="column"
            flex="1"
          >
            <FormLabel sx={{ marginBottom: "4px !important" }}>
              {t("asapProfile.criteria.fields.summary")}
            </FormLabel>
            <Textarea
              size="sm"
              value={form.summary}
              onChange={(e) => setField("summary", e.target.value)}
              data-testid="criteria-input-summary"
              flex="1"
              minH="160px"
              resize="vertical"
            />
          </FormControl>
          <FormControl isDisabled={inputsDisabled}>
            <HStack justify="space-between" mb={1}>
              <FormLabel sx={{ marginBottom: "0 !important" }}>
                {t("asapProfile.criteria.fields.experienceYears")}
              </FormLabel>
              <HStack spacing={2}>
                <Text
                  fontSize="xs"
                  color="gray.600"
                  sx={{ marginBottom: "0 !important" }}
                  data-testid="criteria-experience-indifferent-label"
                >
                  {t("asapProfile.criteria.experienceSlider.indifferent")}
                </Text>
                <Switch
                  size="sm"
                  isChecked={isExperienceAny}
                  onChange={(e) => handleIndifferentToggle(e.target.checked)}
                  data-testid="criteria-experience-indifferent"
                />
              </HStack>
            </HStack>
            <RangeSlider
              min={EXPERIENCE_MIN}
              max={EXPERIENCE_MAX}
              step={1}
              value={[sliderMin, sliderMax]}
              onChange={handleSliderChange}
              isDisabled={inputsDisabled || isExperienceAny}
              data-testid="criteria-experience-slider"
            >
              <RangeSliderTrack>
                <RangeSliderFilledTrack />
              </RangeSliderTrack>
              <RangeSliderThumb
                index={0}
                data-testid="criteria-experience-thumb-min"
              />
              <RangeSliderThumb
                index={1}
                data-testid="criteria-experience-thumb-max"
              />
            </RangeSlider>
            <Text
              fontSize="xs"
              color="gray.600"
              mt={1}
              sx={{ marginBottom: "0 !important" }}
              data-testid="criteria-experience-range-label"
            >
              {isExperienceAny
                ? t("asapProfile.criteria.experienceSlider.anyValue")
                : t("asapProfile.criteria.experienceSlider.range", {
                    min: sliderMin,
                    max: sliderMax,
                  })}
            </Text>
          </FormControl>
        </VStack>
        <VStack align="stretch" spacing={3} h="100%">
          <FormControl isDisabled={inputsDisabled}>
            <FormLabel sx={{ marginBottom: "4px !important" }}>
              {t("asapProfile.criteria.fields.client")}
            </FormLabel>
            <Input
              size="sm"
              value={form.client}
              onChange={(e) => setField("client", e.target.value)}
              data-testid="criteria-input-client"
            />
          </FormControl>
          <Box
            borderWidth="1px"
            borderRadius="md"
            borderColor="gray.200"
            p={3}
            data-testid="geo-section"
          >
            <FormLabel
              fontSize="sm"
              sx={{ marginBottom: "8px !important" }}
            >
              {t("asapProfile.criteria.fields.geoSection")}
            </FormLabel>
            <FormControl isDisabled={inputsDisabled} data-testid="geo-scope">
              <FormLabel
                fontSize="xs"
                color="gray.600"
                sx={{ marginBottom: "4px !important" }}
              >
                {t("asapProfile.criteria.fields.geoScope")}
              </FormLabel>
              <RadioGroup
                size="sm"
                value={form.geo_scope || ""}
                onChange={(value) => setScope(value)}
                data-testid="criteria-radio-scope"
              >
                <VStack align="stretch" spacing={1}>
                  {SCOPE_OPTIONS.map((s) => (
                    <Radio
                      key={s}
                      value={s}
                      data-testid={`criteria-radio-scope-${s}`}
                    >
                      {t(`asapProfile.criteria.geoScope.${s}`)}
                    </Radio>
                  ))}
                </VStack>
              </RadioGroup>
            </FormControl>
            {showCountry && (
              <FormControl
                isDisabled={inputsDisabled}
                mt={3}
                data-testid="geo-country"
              >
                <FormLabel
                  fontSize="xs"
                  color="gray.600"
                  sx={{ marginBottom: "4px !important" }}
                >
                  {t("asapProfile.criteria.fields.geoCountry")}
                </FormLabel>
                <Input
                  size="sm"
                  value={form.geo_country}
                  onChange={(e) => setField("geo_country", e.target.value)}
                  placeholder={t(
                    "asapProfile.criteria.fields.geoCountryPlaceholder"
                  )}
                  data-testid="criteria-input-country"
                />
              </FormControl>
            )}
            {showRegion && (
              <FormControl
                isDisabled={inputsDisabled || regionsLoading}
                mt={3}
                data-testid="geo-region"
              >
                <FormLabel
                  fontSize="xs"
                  color="gray.600"
                  sx={{ marginBottom: "4px !important" }}
                >
                  {t("asapProfile.criteria.fields.geoRegion")}
                </FormLabel>
                <Select
                  size="sm"
                  value={form.geo_region || ""}
                  onChange={(e) => setField("geo_region", e.target.value)}
                  data-testid="criteria-select-region"
                  height="32px"
                  lineHeight="32px"
                  paddingTop="0"
                  paddingBottom="0"
                  sx={{
                    paddingTop: "0 !important",
                    paddingBottom: "0 !important",
                    lineHeight: "32px !important",
                    height: "32px !important",
                  }}
                >
                  <option value="">
                    {t("asapProfile.criteria.fields.geoRegionPlaceholder")}
                  </option>
                  {availableRegions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
                {countryKey && regionsFailedFor[countryKey] && (
                  <Button
                    size="xs"
                    variant="link"
                    mt={1}
                    onClick={retryLoadRegions}
                    data-testid="criteria-retry-regions"
                  >
                    {t("asapProfile.criteria.regionsRetry")}
                  </Button>
                )}
              </FormControl>
            )}
            {showCity && (
              <FormControl
                isDisabled={inputsDisabled}
                mt={3}
                data-testid="geo-city"
              >
                <FormLabel
                  fontSize="xs"
                  color="gray.600"
                  sx={{ marginBottom: "4px !important" }}
                >
                  {t("asapProfile.criteria.fields.geoCity")}
                </FormLabel>
                <Input
                  size="sm"
                  value={form.geo_city}
                  onChange={(e) => setField("geo_city", e.target.value)}
                  placeholder={t(
                    "asapProfile.criteria.fields.geoCityPlaceholder"
                  )}
                  data-testid="criteria-input-city"
                />
              </FormControl>
            )}
          </Box>
        </VStack>
      </SimpleGrid>

      <Box>
        <FormLabel sx={{ marginBottom: "4px !important" }}>
          {t("asapProfile.criteria.fields.skills")}
        </FormLabel>
        <VStack align="stretch" spacing={3} h="100%">
          {TIER_KEYS.map((tier) => (
            <Box key={tier} data-testid={`criteria-skills-${tier}`}>
              <FormLabel
                fontSize="xs"
                color="gray.600"
                sx={{ marginBottom: "4px !important" }}
              >
                {t(`asapProfile.criteria.skillsTiers.${tier}`)}
                {tier === "required" ? " *" : ""}
              </FormLabel>
              <HStack mb={2}>
                <Input
                  size="sm"
                  isDisabled={inputsDisabled}
                  placeholder={t("asapProfile.criteria.skillsPlaceholder")}
                  value={skillInputs[tier].name}
                  onChange={(e) =>
                    setSkillInputs((prev) => ({
                      ...prev,
                      [tier]: { ...prev[tier], name: e.target.value },
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSkill(tier);
                    }
                  }}
                  data-testid={`criteria-skill-input-${tier}`}
                />
                <Select
                  size="sm"
                  width="140px"
                  isDisabled={inputsDisabled}
                  value={skillInputs[tier].type}
                  onChange={(e) =>
                    setSkillInputs((prev) => ({
                      ...prev,
                      [tier]: { ...prev[tier], type: e.target.value },
                    }))
                  }
                  data-testid={`criteria-skill-type-${tier}`}
                  height="32px"
                  lineHeight="32px"
                  paddingTop="0"
                  paddingBottom="0"
                  sx={{
                    paddingTop: "0 !important",
                    paddingBottom: "0 !important",
                    lineHeight: "32px !important",
                    height: "32px !important",
                  }}
                >
                  {TYPE_KEYS.map((type) => (
                    <option key={type} value={type}>
                      {t(`asapProfile.criteria.skillTypes.${type}`)}
                    </option>
                  ))}
                </Select>
                <IconButton
                  size="sm"
                  isDisabled={inputsDisabled}
                  aria-label={t("asapProfile.criteria.addSkillAria")}
                  icon={<FaPlus />}
                  onClick={() => addSkill(tier)}
                  data-testid={`criteria-skill-add-${tier}`}
                />
              </HStack>
              {!inputsDisabled && (form.skills[tier] || []).length > 1 && (
                <Text
                  fontSize="xs"
                  color="gray.500"
                  sx={{ marginBottom: "4px !important" }}
                  data-testid={`criteria-skill-reorder-hint-${tier}`}
                >
                  {t("asapProfile.criteria.skillReorderHint")}
                </Text>
              )}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => {
                  const { active, over } = event;
                  if (!over || active.id === over.id) return;
                  const list = form.skills[tier] || [];
                  const ids = list.map((label) => `${tier}:${label}`);
                  const oldIndex = ids.indexOf(active.id);
                  const newIndex = ids.indexOf(over.id);
                  if (oldIndex !== -1 && newIndex !== -1) {
                    reorderSkill(tier, oldIndex, newIndex);
                  }
                }}
              >
                <SortableContext
                  items={(form.skills[tier] || []).map(
                    (label) => `${tier}:${label}`
                  )}
                  strategy={rectSortingStrategy}
                >
                  <Wrap
                    spacing={2}
                    data-testid={`criteria-skills-list-${tier}`}
                  >
                    {(form.skills[tier] || []).map((label) => {
                      const type = skillTypeFor(label, form.typed);
                      const color = SKILL_TYPE_COLOR[type] || "gray";
                      const isRequired = tier === "required";
                      const toggleTooltip = isRequired
                        ? t("asapProfile.criteria.skillTierToggle.markOptional")
                        : t("asapProfile.criteria.skillTierToggle.markRequired");
                      return (
                        <SortableSkillTag
                          key={`${tier}:${label}`}
                          id={`${tier}:${label}`}
                          tier={tier}
                          label={label}
                          color={color}
                          type={type}
                          isRequired={isRequired}
                          inputsDisabled={inputsDisabled}
                          toggleTooltip={toggleTooltip}
                          reorderAriaLabel={t(
                            "asapProfile.criteria.skillReorderAria",
                            { label }
                          )}
                          onToggle={() => toggleSkillTier(tier, label)}
                          onRemove={() => removeSkill(tier, label)}
                        />
                      );
                    })}
                  </Wrap>
                </SortableContext>
              </DndContext>
            </Box>
          ))}
        </VStack>
      </Box>

      <HStack justify="flex-end" pt={2} spacing={2}>
        {scopeMissing && (
          <Text
            fontSize="xs"
            color="orange.600"
            sx={{ marginBottom: "0 !important" }}
            data-testid="criteria-scope-hint"
          >
            {form.geo_scope === "city"
              ? t("asapProfile.criteria.errors.geoCityRequired")
              : form.geo_scope === "region"
                ? t("asapProfile.criteria.errors.geoRegionRequired")
                : t("asapProfile.criteria.errors.geoCountryRequired")}
          </Text>
        )}
        {isCompleted && (
          <Button
            leftIcon={<FaUsers />}
            variant="outline"
            colorScheme="teal"
            onClick={onViewCandidates}
            data-testid="view-candidates-button"
          >
            {t("asapProfile.profiles.viewCandidatesEmpty")}
          </Button>
        )}
        <Button
          leftIcon={isSubmitting ? null : <FaPlay />}
          colorScheme="teal"
          onClick={handleStart}
          isLoading={isSubmitting}
          isDisabled={!canStart}
          data-testid="start-search-button"
        >
          {isCompleted
            ? t("asapProfile.run.runAgain")
            : t("asapProfile.run.startSearch")}
        </Button>
      </HStack>

      <AlertDialog
        isOpen={confirmRerunOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmRerunOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent data-testid="run-rerun-dialog">
            <AlertDialogHeader sx={{ marginBottom: "0 !important" }}>
              {isDirty
                ? t("asapProfile.run.rerunDialog.titleDirty")
                : t("asapProfile.run.rerunDialog.title")}
            </AlertDialogHeader>
            <AlertDialogBody>
              {isDirty
                ? t("asapProfile.run.rerunDialog.bodyDirty")
                : t("asapProfile.run.rerunDialog.body")}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmRerunOpen(false)}>
                {t("asapProfile.run.rerunDialog.keep")}
              </Button>
              <Button colorScheme="teal" ml={3} onClick={handleConfirmRerun}>
                {isDirty
                  ? t("asapProfile.run.rerunDialog.confirmDirty")
                  : t("asapProfile.run.rerunDialog.confirm")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default CriteriaForm;
